import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import Styles from '../Styles';
import ExistingAddresses from './ExistingAddresses';
import AddressSummary from './AddressSummary';
import PropertyAddressEditor from './PropertyAddressEditor';
import AddressEditor from './AddressEditor';
import Button from '../Parts/Button';
import type { ExistingAddress, RootWithAlert } from './addressTypes';

type AddressModalProps = {
	ROOT: RootWithAlert;
	addressLabel: string;
	address: ExistingAddress | undefined;
	onDone: () => void;
	getAddressesFunction: string;
	setAddress: (address: ExistingAddress) => void;
	isPropertyAddress: boolean | undefined;
	quoteType: string;
	openEditorOnLoad?: boolean;
	prefillAddress?: ExistingAddress | null;
};

const AddressModal = ({
	ROOT,
	addressLabel,
	address,
	onDone,
	getAddressesFunction,
	setAddress,
	isPropertyAddress,
	quoteType,
	openEditorOnLoad = false,
	prefillAddress = null,
}: AddressModalProps) => {
	const [showingAddressEditor, setShowingAddressEditor] = useState(openEditorOnLoad);
	const [usingAddress, setUsingAddress] = useState<ExistingAddress | null>(prefillAddress);

	const set = (newAddress: ExistingAddress) => {
		setAddress(newAddress);
		onDone();
	};

	const setUsing = (newAddress: ExistingAddress) => {
		const { id: _id, ...addr } = newAddress;

		setUsingAddress({ ...addr });
		setShowingAddressEditor(true);
	};

	return (
		<div id="address-modal-wrapper">
			{/* Start of First Modal (select address from previous addresses or button to add a new one) */}
			<Container className="local-bootstrap" fluid style={{ height: '100vh', overflowY: 'auto' }}>
				<div
					className="quote-address address-modal address-modal-one"
					style={{ width: '100vw' }}
				>
					<Row>
						<Col>
							<h4 className={`text-${quoteType}`}>{addressLabel}</h4>
						</Col>
						<Col className="text-end">
							<Button
								color={quoteType ?? 'green'}
								onClick={() => {
									setUsingAddress(null);
									setShowingAddressEditor(true);
								}}
							>
								+ New Address
							</Button>
						</Col>
					</Row>
					{isPropertyAddress !== undefined ? (
						<Row style={{ color: Styles.psGray, margin: '7px 0' }}>
							<Col>
								This is not the shipping address. This address will be used as the
								property in the PowerShades dashboard.
							</Col>
						</Row>
					) : null}

					<div className="mt-2">
						<h5
							className={`text-${quoteType}`}
						>
							Current Address
						</h5>
						<Row>
							<Col>
								<AddressSummary address={address} isPropertyAddress={isPropertyAddress} />
							</Col>
						</Row>
						<Row>
							<Col className="text-end">
								<Button
									color={quoteType ?? 'green'}
									onClick={() => {
										if (address) {
											setUsingAddress(address);
										}
										setShowingAddressEditor(true);
									}}
								>
									Edit
								</Button>
							</Col>
						</Row>
					</div>
					
					<ExistingAddresses
						quoteType={quoteType}
						getAddressesFunction={getAddressesFunction}
						createNewAddress={() => setShowingAddressEditor(true)}
						useAddress={(a) => setUsing(a)}
					/>

					<Row>
						<Col className="text-end">
							<Button
								color={quoteType ?? 'green'}
								onClick={onDone}
							>
								Done
							</Button>
						</Col>
					</Row>
				</div>

				{showingAddressEditor ? (
					isPropertyAddress ? (
						<PropertyAddressEditor
							quoteType={quoteType}
							label={addressLabel}
							initialAddress={usingAddress}
							onCancel={() => setShowingAddressEditor(false)}
							onSet={set}
							ROOT={ROOT}
						/>
					) : (
						<AddressEditor
							quoteType={quoteType}
							label={addressLabel}
							initialAddress={usingAddress}
							onCancel={() => setShowingAddressEditor(false)}
							onSet={set}
						/>
					)
				) : null}
			</Container>
		</div>
	);
};

export default AddressModal;
